import { Badge, BADGE_TYPE, LottiePlayer } from '@repo/design-system-kit';
import { useIntl } from 'react-intl';
import upsellPopup from '@repo/visual-assets/animations/lottie_cp_general_upsell.lottie';
import cx from 'clsx';
import { usePricingOffer } from '@repo/shared-kit/pricing/hooks';
import { PricingOffer } from '../pricing-offer';
import type { AnnualPrice, Price, Recurrence } from '@repo/shared-kit/pricing/types';
import styles from './comparison-pricing-offer.module.css';

export interface ComparisonPricingOfferProps {
  subtitle: string;
  priceCaption?: string | null;
  title: string;
  pricingCaption: string;
  monthlyPrice: Price;
  annualPrice: AnnualPrice;
  recurrence: Recurrence;
  variant: 'trial' | 'upgrade' | 'promo';
  freeTrialText: string;
}

const badgeTypes = {
  trial: BADGE_TYPE.TRY_FOR_FREE,
  upgrade: BADGE_TYPE.SAVE,
  promo: BADGE_TYPE.LIMITED_TIME,
};

export function ComparisonPricingOffer({
  title,
  subtitle,
  priceCaption,
  pricingCaption,
  variant,
  monthlyPrice,
  annualPrice,
  recurrence,
  freeTrialText,
}: ComparisonPricingOfferProps) {
  const { formatMessage } = useIntl();
  const { price, strikethroughPrice } = usePricingOffer({
    monthlyPrice,
    annualPrice,
    recurrence,
    freeTrialText,
    variant,
  });
  const badgeTexts = {
    trial: formatMessage({ id: 'generic.promotions.try-for-free.badge' }),
    upgrade: formatMessage({ id: 'generic.promotions.spend-smart.badge' }),
    promo: formatMessage({ id: 'shared-kit.pricing.promotion-system.badge.limited-time' }),
  };

  return (
    <div className={cx(styles.container, styles[variant])} data-testid="comparison-pricing-offer">
      <LottiePlayer className={styles.lottieAnimation} loop={false} path={upsellPopup} />
      <Badge className={styles.badge} text={badgeTexts[variant]} type={badgeTypes[variant]} />
      <p className="heading-section">{title}</p>
      <p className="body-standard">{subtitle}</p>
      <PricingOffer
        caption={pricingCaption}
        price={price}
        priceCaption={priceCaption}
        strikethroughPrice={strikethroughPrice}
        subscriptionCaption={recurrence === 'annual' ? annualPrice.billingRecurrence : null}
      />
    </div>
  );
}
